/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react'
import { Box, Typography, Stack, FormControl, TextField } from '@mui/material'

// icons
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined'
import AccessAlarmsOutlinedIcon from '@mui/icons-material/AccessAlarmsOutlined'
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined'
import ImportContactsTwoToneIcon from '@mui/icons-material/ImportContactsTwoTone'
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined'
import { draggedElementStyles } from '../../styles/classes/FormBuilderClasses'
import { useTranslation } from 'react-i18next'
import { faICursor } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  checkNotFullfilled,
  elementTypes,
  getFirstKeyFromObject,
  elementIcons
} from '../../utils/formBuilder'
import customTheme from '../../styles/mui_theme'

const DraggedElement = ({
  item,
  selected,
  alreadySelected,
  mandatoryFields,
  onChangeTitle,
  onSelectItem,
  disableEdit,
  recentlyAdded
}) => {
  const [missingData, setMissingData] = useState(false)
  const [borderColor, setBorderColor] = useState(
    customTheme.colors.logInDisabledBackground
  )

  const { t } = useTranslation()
  const { classes } = draggedElementStyles()
  const questionObj = item[getFirstKeyFromObject(item)]
  const title = questionObj.title
  const [localTitle, setLocalTitle] = useState(title ?? '')
  const helpfulHint = questionObj.helpful_hint
  let requiredField = questionObj.required
  const itemType = questionObj.type
  const enabledNestedLogic = questionObj.nested_logic
  const hasDependency = questionObj?.dependency
  const timeStamp =
    questionObj?.time_stamp ||
    itemType === elementTypes.PHOTOS ||
    itemType === elementTypes.ATTACHMENTS // needs to be always active on PHOTO or ATTACHMENT element
  let totalAmountDueEnabled = false
  if (
    (itemType === elementTypes.LINE_ITEMS ||
      itemType === elementTypes.MATERIAL_PARTS) &&
    questionObj?.total_amount_due === true
  ) {
    totalAmountDueEnabled = true
  }

  if (itemType === elementTypes.AUTHORIZATION_FIELD && requiredField === 'disable') {
    requiredField = false
  }
  useEffect(() => {
    let missingFieldsCount = 0
    for (const field of mandatoryFields) {
      // check if question has key field
      if (Object.prototype.hasOwnProperty.call(questionObj, field)) {
        const notFullfilled = checkNotFullfilled(questionObj, field)
        if (notFullfilled) missingFieldsCount++
      }
    }
    if (missingFieldsCount !== 0) setMissingData(true)
    else setMissingData(false)
  }, [selected])

  useEffect(() => {
    // border color red if not selected and
    // was selected before  and
    // has missing fields
    setBorderColor(customTheme.colors.logInDisabledBackground)
    if (selected) setBorderColor(customTheme.colors.iconBlue)
    if (!selected && alreadySelected && missingData) {
      setBorderColor(customTheme.colors.redError)
    }
  }, [selected, alreadySelected, missingData])

  useEffect(() => {
    setLocalTitle(title)
  }, [title])

  const changeTitleHandler = event => {
    onChangeTitle(event.target.value)
    setLocalTitle(event.target.value)
  }

  return (
    <Box
      sx={{
        border: `1px solid ${borderColor}`,
        marginLeft: hasDependency && '20px',
        boxShadow:
          recentlyAdded &&
          `${customTheme.colors.iconBlueLight} 0px 0px 0px 3px inset;`,
        transition: 'box-shadow 0.6s linear'
      }}
      className={classes.container}
      onClick={() => {
        onSelectItem(item)
      }}
    >
      <FormControl className={classes.form}>
        {disableEdit
          ? (
          <Box>
            <Typography variant="h6" className={classes.disabledTitle}>
              {localTitle}
            </Typography>
          </Box>
            )
          : (
          <TextField
            disabled={disableEdit}
            id={item.id}
            variant="standard"
            className={classes.titlePlaceholder}
            InputProps={{ disableUnderline: true }}
            placeholder={t('form_builder.elements.enter_title_placeholder')}
            onChange={changeTitleHandler}
            value={localTitle}
          />
            )}
      </FormControl>

      <Box className={classes.elementContainer}>
        <Box className={classes.itemNameContainer}>
          <Box className={classes.cursorIcon}>
            {itemType in elementIcons && (
              <FontAwesomeIcon icon={elementIcons[itemType]} />
            )}
            {!(itemType in elementIcons) && (
              <FontAwesomeIcon icon={faICursor} />
            )}
          </Box>
          {itemType === elementTypes.AUTHORIZATION_FIELD && <Typography className={classes.itemName}>
            {t('form_builder.elements.authorization_field_subtitle')}
          </Typography>}
          {itemType !== elementTypes.AUTHORIZATION_FIELD && <Typography className={classes.itemName}>
            {t(`form_builder.elements.${itemType}`)}
          </Typography>}

        </Box>
        <Stack direction="row" spacing={2}>
          {totalAmountDueEnabled && (
            <Box className={classes.iconTextContainer}>
              <AttachMoneyOutlinedIcon className={classes.iconEnabled} />
              <Typography className={classes.fontEnabled}>
                {t('form_builder.elements.total_amount_due')}
              </Typography>
            </Box>
          )}
          <Box className={classes.iconTextContainer}>
            <HelpOutlineOutlinedIcon
              className={
                helpfulHint ? classes.iconEnabled : classes.iconDisabled
              }
            />
            <Typography
              className={
                helpfulHint ? classes.fontEnabled : classes.fontDisabled
              }
            >
              {t('form_builder.dragged_element.helpful_hint')}
            </Typography>
          </Box>
          <Box className={classes.iconTextContainer}>
            <AccessAlarmsOutlinedIcon
              className={timeStamp ? classes.iconEnabled : classes.iconDisabled}
            />
            <Typography
              className={timeStamp ? classes.fontEnabled : classes.fontDisabled}
            >
              {t('form_builder.dragged_element.time_stamp')}
            </Typography>
          </Box>
          <Box className={classes.iconTextContainer}>
            <CheckBoxOutlinedIcon
              className={
                requiredField ? classes.iconEnabled : classes.iconDisabled
              }
            />
            <Typography
              className={
                requiredField ? classes.fontEnabled : classes.fontDisabled
              }
            >
              {t('form_builder.dragged_element.required_field')}
            </Typography>
          </Box>
          <Box className={classes.iconTextContainer}>
            <ImportContactsTwoToneIcon
              className={
                enabledNestedLogic ? classes.iconEnabled : classes.iconDisabled
              }
            />
            <Typography
              className={
                enabledNestedLogic ? classes.fontEnabled : classes.fontDisabled
              }
            >
              {t('form_builder.dragged_element.logic')}
            </Typography>
          </Box>
        </Stack>
      </Box>
    </Box>
  )
}

export default DraggedElement
